import getTextBlockType, { getTextBlocks } from './Parser';
import IdManager from '../tools/IdManager';
import TextBlock, { TextBlockTypes } from './TextBlock';
import isDescendant from '../tools/isDescendant';
import { getSelection, setSelection } from './TextBlockSelection';
const redoStackSize = 20;
export default class ContentEditableText {
    constructor(element, text, onBlur, toggleUnsavedChanges) {
        this.scrollableParent = null;
        if ('true' !== element.contentEditable) {
            throw new Error(`Element needs to be contentEditable.`);
        }
        if (element.spellcheck) {
            console.warn(`Consider disabling spellcheck on the contentEditable for performance reasons.`);
        }
        this.toggleUnsavedChanges = toggleUnsavedChanges;
        this.element = element;
        this.idManager = new IdManager('textblock');
        this.state = { blocks: getTextBlocks(text, this.idManager), selection: undefined };
        this.reRender();
        this.undoStack = [];
        const selectionChange = () => {
            try {
                this.state.selection = getSelection(this.element);
            }
            catch (_a) {
                this.state.selection = undefined;
            }
        };
        document.addEventListener('selectionchange', selectionChange);
        const onInput = this.onInput.bind(this);
        this.element.addEventListener('input', onInput);
        this.element.addEventListener('input', onInput);
        const onKeypress = (e) => {
            if (this.hasSelectionOverMultipleBlocks()) {
                this.preChangeStateHook(true, false);
                if (this.handleInputOnRangeBlockSelection(this.state.selection, e.key)) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };
        this.element.addEventListener('keypress', onKeypress);
        const onKeyDown = (e) => {
            if (e.ctrlKey && e.key === 'z') {
                e.preventDefault();
                e.stopPropagation();
                this.undo();
            }
            if (e.key === 'Enter') {
                if (this.handleEnter(e.ctrlKey || e.shiftKey)) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
            if (e.key === 'Backspace') {
                if (this.handleBackspace()) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };
        this.element.addEventListener('keydown', onKeyDown);
        const onPaste = (e) => {
            this.preChangeStateHook(true, false);
            const clipboardData = e.clipboardData;
            const pastedText = clipboardData.getData('text/plain');
            let selection = getSelection(this.element);
            if (selection.isRange) {
                if (selection.startBlockId === selection.endBlockId) {
                    const startBlock = this.getBlockFromId(selection.startBlockId);
                    const oldText = startBlock.getText();
                    const newText = oldText.substr(0, selection.startPosition) + oldText.substr(selection.endPosition, oldText.length);
                    this.updateBlockText(selection.startBlockId, newText, selection.startPosition);
                }
                else {
                    this.handleInputOnRangeBlockSelection(selection, '');
                }
            }
            selection = getSelection(this.element);
            const lines = pastedText.split(/\r?\n/);
            if (lines.length > 0) {
                const startBlock = this.getBlockFromId(selection.startBlockId);
                const oldText = startBlock.getText();
                let newText = oldText.substr(0, selection.startPosition) + lines.shift();
                let caretPosition = newText.length;
                let lastText = oldText.substr(selection.endPosition, oldText.length);
                if (lines.length === 0) {
                    newText += lastText;
                    lastText = '';
                }
                this.updateBlockText(startBlock.getId(), newText, caretPosition);
                let previousIndex = this.getBlockIndexFromId(startBlock.getId());
                let previousType = startBlock.getType();
                let previousElement = this.getElementFromBlockId(startBlock.getId());
                while (lines.length > 0) {
                    let line = lines.shift();
                    if (lines.length === 0) {
                        line += lastText;
                    }
                    let nextLine = undefined;
                    if (lines.length !== 0) {
                        nextLine = lines[0];
                    }
                    else {
                        nextLine = this.state.blocks[previousIndex + 1] ? this.state.blocks[previousIndex + 1].getText() : undefined;
                    }
                    const type = getTextBlockType(line, previousType, nextLine);
                    previousType = type;
                    const newBlock = new TextBlock(this.idManager.getNextId(), type, line);
                    [previousElement, previousIndex] = this.insertBlock(newBlock, previousIndex, previousElement);
                    this.setCaret(previousElement, line.length);
                }
            }
            e.stopPropagation();
            e.preventDefault();
        };
        this.element.addEventListener('paste', onPaste);
        const onDragStart = (e) => {
            e.preventDefault();
        };
        this.element.addEventListener('dragstart', onDragStart); // prevent drag and drop of text
        const blurCallback = () => {
            if (onBlur) {
                onBlur(this.getText());
            }
        };
        this.element.addEventListener('blur', blurCallback);
        this.onDestroy = () => {
            document.removeEventListener('selectionchange', selectionChange);
            this.element.removeEventListener('input', onInput);
            this.element.removeEventListener('keypress', onKeypress);
            this.element.removeEventListener('keydown', onKeyDown);
            this.element.removeEventListener('paste', onPaste);
            this.element.removeEventListener('dragstart', onDragStart);
            this.element.removeEventListener('blur', blurCallback);
        };
    }
    getScrollParent(node) {
        if (node === null) {
            return null;
        }
        if (node.scrollHeight > node.clientHeight) {
            return node;
        }
        else {
            return this.getScrollParent(node.parentElement);
        }
    }
    destroy() {
        this.onDestroy();
    }
    hasSelectionOverMultipleBlocks() {
        return !!this.state.selection && this.state.selection.isRange && this.state.selection.startBlockId !== this.state.selection.endBlockId;
    }
    handleBackspace() {
        if (this.state.selection && !this.state.selection.isRange && this.state.selection.startPosition === 0) { // Merge with previous element when caret at beginning.
            this.preChangeStateHook(true, false);
            const selection = this.state.selection;
            const element = this.getElementFromBlockId(selection.startBlockId);
            if (element) {
                const previousElement = element.previousElementSibling;
                if (previousElement) {
                    const textToAddToPrevious = element.textContent;
                    const [previousBlock, previousIndex] = this.getBlockFromElement(previousElement);
                    this.removeBlockById(selection.startBlockId);
                    let newText = previousBlock.getText();
                    const afterCaretPosition = newText.length;
                    newText += textToAddToPrevious;
                    previousElement.innerHTML = newText;
                    this.onElementChanged(previousElement);
                    this.setCaret(previousElement, afterCaretPosition);
                    return true;
                }
                else {
                    // we don't have a previous element, so we just do nothing and say it's handled
                    return true;
                }
            }
        }
        if (this.hasSelectionOverMultipleBlocks()) { // Remove selected stuff
            this.preChangeStateHook(true, false);
            if (this.handleInputOnRangeBlockSelection(this.state.selection, '')) {
                return true;
            }
        }
        return false;
    }
    handleEnter(ctrlKey) {
        const element = this.getCurrentElement();
        if (!element) {
            console.error(`Could not find the currently edited block for Enter.`);
            return false;
        }
        this.preChangeStateHook(true, false);
        let selection = this.state.selection;
        if (selection && selection.isRange) {
            if (selection.startBlockId === selection.endBlockId) {
                const startBlock = this.getBlockFromId(selection.startBlockId);
                const oldText = startBlock.getText();
                const newText = oldText.substr(0, selection.startPosition) + oldText.substr(selection.endPosition, oldText.length);
                this.updateBlockText(selection.startBlockId, newText, selection.startPosition);
                // Don't return as we will go on to split away at the caret.
            }
            else {
                this.handleInputOnRangeBlockSelection(selection, '');
                // Don't return as we will go on to split away at the caret.
            }
        }
        this.insertNewLineAfterEnter(element, ctrlKey);
        const target = this.getCurrentElement();
        if (target.getBoundingClientRect().bottom + 22 > window.innerHeight) {
            target.scrollIntoView(false);
            if (!this.scrollableParent) {
                this.scrollableParent = this.getScrollParent(this.element);
            }
            if (this.scrollableParent) {
                this.scrollableParent.scrollTop += 22; // line height
            }
        }
        if (target.getBoundingClientRect().top < 0) {
            target.scrollIntoView();
        }
        return true;
    }
    insertNewLineAfterEnter(element, ctrlKey = false) {
        const [block, index] = this.getBlockFromElement(element);
        let textForNewElement = '';
        const selection = getSelection(this.element);
        if (selection && !selection.isRange && selection.startBlockId === block.getId()) {
            const oldText = block.getText();
            const newText = oldText.substr(0, selection.startPosition);
            textForNewElement = oldText.substr(selection.startPosition);
            this.updateBlockText(block.getId(), newText);
        }
        this.reEvaluateBlock(block, index, element);
        let newType = TextBlockTypes.Empty;
        if (ctrlKey && '' === textForNewElement) {
            switch (block.getType()) {
                case TextBlockTypes.BulletPointLevel1:
                case TextBlockTypes.DoneBulletPointLevel1:
                    newType = TextBlockTypes.BulletPointLevel1;
                    textForNewElement = '* ';
                    break;
                case TextBlockTypes.BulletPointLevel2:
                case TextBlockTypes.DoneBulletPointLevel2:
                    newType = TextBlockTypes.BulletPointLevel2;
                    textForNewElement = '** ';
                    break;
                case TextBlockTypes.BulletPointLevel3:
                case TextBlockTypes.DoneBulletPointLevel3:
                    newType = TextBlockTypes.BulletPointLevel3;
                    textForNewElement = '*** ';
                    break;
                default:
                    newType = TextBlockTypes.Empty;
                    break;
            }
        }
        // const addAnotherLine = !ctrlKey && [TextBlockTypes.Header1, TextBlockTypes.Header2, TextBlockTypes.Header3, TextBlockTypes.Header4, TextBlockTypes.Header5, TextBlockTypes.Header6].indexOf(block.getType()) !== -1;
        const addAnotherLine = false;
        const newBlockText = (!addAnotherLine ? textForNewElement : '');
        if (newBlockText !== '' || block.getText() === '') {
            newType = getTextBlockType(newBlockText, block.getType(), this.state.blocks[index + 1] ? this.state.blocks[index + 1].getText() : undefined);
        }
        const newBlock = new TextBlock(this.idManager.getNextId(), newType, newBlockText);
        const [newElement, newIndex] = this.insertBlock(newBlock, index, element);
        if (addAnotherLine) {
            let anotherType = TextBlockTypes.Empty;
            if ('' !== textForNewElement) {
                anotherType = getTextBlockType(textForNewElement, newType, this.state.blocks[index + 1] ? this.state.blocks[index + 1].getText() : undefined);
            }
            const anotherBlock = new TextBlock(this.idManager.getNextId(), anotherType, textForNewElement);
            const [anotherElement, anotherIndex] = this.insertBlock(anotherBlock, newIndex, newElement);
            this.reEvaluateBlock(anotherBlock, anotherIndex, anotherElement);
        }
        else {
            this.reEvaluateBlock(newBlock, newIndex, newElement);
        }
        if (newBlockText.length > 0) {
            this.setCaret(newElement, newBlockText.length);
        }
    }
    handleInputOnRangeBlockSelection(selection, addedText = '') {
        if (!selection) {
            return false;
        }
        let handled = false;
        if (selection.endBlockId === selection.startBlockId) {
            return handled;
        }
        selection.inBetweenBlockIds.forEach((blockId) => {
            this.removeBlockById(blockId);
        });
        let addToFirstElement = addedText;
        const cursorAfter = selection.startPosition + addToFirstElement.length;
        if (!selection.endCompletelySelected) {
            const endBlock = this.getBlockFromId(selection.endBlockId);
            addToFirstElement += endBlock.getText().substr(selection.endPosition);
        }
        this.removeBlockById(selection.endBlockId);
        const firstBlock = this.getBlockFromId(selection.startBlockId);
        const newText = firstBlock.getText().substr(0, selection.startPosition) + addToFirstElement;
        const element = this.getElementFromBlockId(selection.startBlockId);
        if (element) {
            element.innerHTML = newText;
            this.onElementChanged(element);
            this.setCaret(element, cursorAfter);
        }
        handled = true;
        return handled;
    }
    updateBlockText(blockId, text, setCaretPosition) {
        const element = this.getElementFromBlockId(blockId);
        if (element) {
            element.innerHTML = text;
            this.onElementChanged(element);
            if (setCaretPosition) {
                this.setCaret(element, setCaretPosition);
            }
        }
    }
    insertBlock(block, indexBefore, elementBefore) {
        const element = block.createBlockElement();
        const newIndex = indexBefore + 1;
        this.element.insertBefore(element, null !== elementBefore ? elementBefore.nextElementSibling : null);
        this.state.blocks.splice(newIndex, 0, block);
        this.setCaret(element, 0);
        return [element, newIndex];
    }
    removeBlockById(blockId) {
        var _a;
        const index = this.getBlockIndexFromId(blockId);
        this.state.blocks.splice(index, 1);
        (_a = this.getElementFromBlockId(blockId)) === null || _a === void 0 ? void 0 : _a.remove();
    }
    preChangeStateHook(forceCommitToUndoStack = false, allowMergeWithOtherChanges = true) {
        this.toggleUnsavedChanges();
        if (!allowMergeWithOtherChanges) {
            this.pushToUndoStack();
        }
        if (!this.previousState) {
            this.previousState = { blocks: [...this.state.blocks], selection: this.state.selection ? Object.assign(Object.assign({}, this.state.selection), { inBetweenBlockIds: [...this.state.selection.inBetweenBlockIds] }) : undefined };
        }
        const currentTime = Date.now();
        const commit = forceCommitToUndoStack || (this.previousStateTimestamp && currentTime - this.previousStateTimestamp > 2000);
        if (commit) {
            this.pushToUndoStack();
        }
        this.previousStateTimestamp = currentTime;
    }
    pushToUndoStack() {
        if (this.previousState) {
            this.undoStack.push(this.previousState);
        }
        this.previousState = undefined;
        this.previousStateTimestamp = undefined;
        if (this.undoStack.length > redoStackSize) {
            this.undoStack.splice(0, this.undoStack.length - redoStackSize);
        }
    }
    undo() {
        if (this.previousState) {
            this.state = this.previousState;
            this.previousState = undefined;
            this.previousStateTimestamp = undefined;
            this.reRender();
            return;
        }
        const popped = this.undoStack.pop();
        if (popped) {
            this.state = popped;
            this.reRender();
        }
    }
    getText() {
        if (!this.isDomInSyncWithBlocks()) {
            alert('DOM was not in sync with state.');
        }
        return this.state.blocks.map((block) => {
            return block.getText();
        }).join("\n");
    }
    reRender() {
        this.element.innerHTML = '';
        if (this.state.blocks.length === 0) {
            const newBlock = new TextBlock(this.idManager.getNextId(), TextBlockTypes.Empty, '');
            this.insertBlock(newBlock, -1, null);
            return;
        }
        this.state.blocks.forEach((block) => {
            const blockElement = block.createBlockElement();
            this.element.append(blockElement);
        });
        if (this.state.selection) {
            this.setSelection(this.state.selection);
        }
        else if (this.element.firstElementChild) {
            this.setCaret(this.element.firstElementChild, 0);
        }
    }
    onInput() {
        const element = this.getCurrentElement();
        if (element) {
            this.preChangeStateHook();
            this.state.selection = getSelection(this.element);
            this.onElementChanged(element);
            if (this.state.selection) {
                this.setSelection(this.state.selection);
            }
        }
    }
    setSelection(selection) {
        const start = this.getElementFromBlockId(selection.startBlockId);
        if (!start) {
            throw new Error(`Start block with ID ${selection.startBlockId} not found.`);
        }
        const end = this.getElementFromBlockId(selection.endBlockId);
        setSelection(start, selection.startPosition, end ? end : undefined, end ? selection.endPosition : undefined);
        this.state.selection = getSelection(this.element);
    }
    setCaret(element, position) {
        setSelection(element, position);
        this.state.selection = getSelection(this.element);
    }
    onElementChanged(element) {
        const [block, index] = this.getBlockFromElement(element);
        this.reEvaluateBlock(block, index, element);
    }
    getBlockFromElement(element) {
        const blockId = element.getAttribute('data-id');
        if (!blockId) {
            throw new Error(`No block found for element`);
        }
        const index = this.getBlockIndexFromId(blockId);
        if (index < 0) {
            throw new Error(`No block found for element with ID ${blockId}`);
        }
        return [this.state.blocks[index], index];
    }
    getElementFromBlockId(blockId) {
        const element = this.element.querySelector('[data-id="' + blockId + '"]');
        return element || undefined;
    }
    getBlockIndexFromId(blockId) {
        return this.state.blocks.findIndex((candidate) => {
            return candidate.getId() === blockId;
        });
    }
    getBlockFromId(blockId) {
        return this.state.blocks[this.getBlockIndexFromId(blockId)];
    }
    reEvaluateBlock(block, index, element) {
        if (!element) {
            element = this.getElementFromBlockId(block.getId());
        }
        if (!element) {
            throw new Error(`No element found for block with ID ${block.getId()}`);
        }
        if (!index) {
            index = this.state.blocks.findIndex((candidate) => {
                return candidate.getId() === block.getId();
            });
        }
        const blockBefore = this.state.blocks[index - 1];
        const blockAfter = this.state.blocks[index + 1];
        const text = element.textContent ? element.textContent : '';
        const type = getTextBlockType(text, blockBefore ? blockBefore.getType() : undefined, blockAfter ? blockAfter.getText() : undefined);
        const changedType = block.getType() !== type;
        const newBlock = new TextBlock(block.getId(), type, text);
        this.state.blocks.splice(index, 1, newBlock);
        newBlock.updateBlockElement(element);
        if (changedType) {
            if (blockBefore) {
                this.reEvaluateBlock(blockBefore);
            }
            if (blockAfter) {
                this.reEvaluateBlock(blockAfter);
            }
        }
    }
    getCurrentElement() {
        const sel = window.getSelection();
        if (!sel) {
            return null;
        }
        const range = sel.getRangeAt(0);
        let element;
        if (range.startContainer.nodeType === 1) {
            element = range.startContainer;
        }
        else {
            element = range.startContainer.parentElement;
        }
        if ((element === null || element === void 0 ? void 0 : element.tagName.toLowerCase()) !== 'div') {
            element = (element === null || element === void 0 ? void 0 : element.closest('div')) || null;
        }
        if (null === element) {
            return null;
        }
        if (!isDescendant(this.element, element)) {
            return null;
        }
        return element;
    }
    isDomInSyncWithBlocks() {
        const divs = [...this.element.querySelectorAll('.TextBlock')];
        if (divs.length !== this.state.blocks.length) {
            console.error(`${divs.length} blocks in DOM vs ${this.state.blocks.length} in state.`);
            return false;
        }
        try {
            divs.forEach((div, index) => {
                const block = this.state.blocks[index];
                if (!block) {
                    throw new Error(`Block with index ${index} was not found in state. Div was ${div.textContent}`);
                }
                if (block.getId() !== div.getAttribute('data-id')) {
                    throw new Error(`Block ID ${block.getId()} did not match DIV ID ${div.getAttribute('data-id')}`);
                }
                if (block.getClassNames().join(' ') !== div.getAttribute('class')) {
                    throw new Error(`Block class ${block.getClassNames().join(' ')} did not match DIV class ${div.getAttribute('class')}`);
                }
                if (block.getText().trim() !== div.textContent.trim()) {
                    throw new Error(`Block ID ${block.getId()} did not match text: Block "${block.getText()}" vs DIV "${div.textContent}"`);
                }
            });
        }
        catch (e) {
            console.error(e);
            return false;
        }
        return true;
    }
}
